import type { GeoMarkupStore } from '../index';

// ===== TASK FILTER SELECTORS =====

/**
 * Select all task filter state
 */
export const selectTaskFilters = (state: GeoMarkupStore) => state.taskFilters;

/**
 * Select task search text filter
 */
export const selectTaskFilterSearch = (state: GeoMarkupStore): string =>
  state.taskFilters.search;

/**
 * Check if any task filters are active (excluding pagination/sort)
 */
export const selectHasActiveTaskFilters = (state: GeoMarkupStore): boolean => {
  const { search, status, priority, assignedToId, dateFrom, dateTo, location } =
    state.taskFilters;
  return !!(
    search ||
    status.length > 0 ||
    priority.length > 0 ||
    assignedToId.length > 0 ||
    dateFrom !== null ||
    dateTo !== null ||
    location
  );
};

/**
 * Excludes `search` because on the map view the SearchBar lives OUTSIDE the
 * collapsible Filters panel — the user already sees it, so it must not light
 * up the filters-dot on the toggle button.
 */
export const selectHasActiveTaskFiltersInMapPanel = (
  state: GeoMarkupStore
): boolean => {
  const { status, priority, assignedToId, dateFrom, dateTo, location } =
    state.taskFilters;
  return !!(
    status.length > 0 ||
    priority.length > 0 ||
    assignedToId.length > 0 ||
    dateFrom !== null ||
    dateTo !== null ||
    location
  );
};

/**
 * Serialized key of task filter state — suitable as useEffect dependency.
 * Includes search and all filters (not page/pageSize).
 */
export const selectTaskFilterKey = (state: GeoMarkupStore): string => {
  const {
    search,
    status,
    priority,
    assignedToId,
    dateFrom,
    dateTo,
    location,
    sortBy,
    sortDescending,
  } = state.taskFilters;
  return `${search}|${status.join(',')}|${priority.join(',')}|${assignedToId.join(',')}|${dateFrom?.getTime() ?? ''}|${dateTo?.getTime() ?? ''}|${location}|${sortBy}|${sortDescending}`;
};

/**
 * Check if advanced filters are active (excluding search and status tabs).
 * Used by FilterIconButton to show the active-filters dot.
 */
export const selectHasAdvancedTaskFilters = (
  state: GeoMarkupStore
): boolean => {
  const { priority, assignedToId, dateFrom, dateTo, location } =
    state.taskFilters;
  return !!(
    priority.length > 0 ||
    assignedToId.length > 0 ||
    dateFrom !== null ||
    dateTo !== null ||
    location
  );
};

/**
 * Used on list/kanban views where the SearchBar lives INSIDE the collapsible
 * Filters panel — so a non-empty search must light the filter-button dot.
 * Status stays excluded because status tabs render separately in the header.
 */
export const selectHasActiveTaskFiltersInListPanel = (
  state: GeoMarkupStore
): boolean => selectHasAdvancedTaskFilters(state) || !!state.taskFilters.search;

/**
 * Count the number of active advanced task filter categories.
 * Each filter type counts as 1 regardless of how many values are selected.
 */
export const selectActiveTaskFilterCount = (state: GeoMarkupStore): number => {
  const { priority, assignedToId, dateFrom, dateTo, location } =
    state.taskFilters;
  let count = 0;
  if (priority.length > 0) count++;
  if (assignedToId.length > 0) count++;
  if (dateFrom !== null || dateTo !== null) count++;
  if (location) count++;
  return count;
};
